/**
 * Orchestrates the offline queue:
 *  - exposes `pendingCount`, `deadCount`, `currentConflict`
 *  - auto-flushes on reconnect
 *  - provides manual `retryNow` and conflict resolvers
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  countPending,
  listMutations,
  flushQueue,
  resolveConflictKeepMine,
  resolveConflictUseTheirs,
  type ConflictInfo,
  OFFLINE_EVENTS,
} from '@/lib/offlineQueue'
import { useAuthContext } from '@/contexts/AuthContext'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { trackConversion } from '@/utils/analytics'

export function useOfflineSync() {
  const { user } = useAuthContext()
  const net = useNetworkStatus()
  const [pendingCount, setPendingCount] = useState(0)
  const [deadCount, setDeadCount] = useState(0)
  const [currentConflict, setCurrentConflict] = useState<ConflictInfo | null>(null)
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null)
  const flushingRef = useRef(false)

  // Refresh counts from IndexedDB
  const refresh = useCallback(async () => {
    if (!user?.id) return
    const pending = await countPending(user.id)
    const all = await listMutations({ userId: user.id })
    setPendingCount(pending)
    setDeadCount(all.filter(m => m.status === 'dead').length)
    // Surface an unresolved conflict if present and not already shown
    const conflictMut = all.find(m => m.status === 'conflict')
    if (conflictMut && !currentConflict) {
      // We don't have server state here — frontend will fetch it when user opens modal
      setCurrentConflict({ mutation: conflictMut, serverState: null } as any)
    }
  }, [user?.id, currentConflict])

  // Listen to queue events
  useEffect(() => {
    const onChange = () => refresh()
    const onConflict = (e: Event) => {
      const detail = (e as CustomEvent<ConflictInfo>).detail
      setCurrentConflict(detail)
    }
    const onCompleted = () => {
      setLastSyncAt(new Date())
      refresh()
    }
    window.addEventListener(OFFLINE_EVENTS.queueChanged, onChange)
    window.addEventListener(OFFLINE_EVENTS.conflict, onConflict as EventListener)
    window.addEventListener(OFFLINE_EVENTS.syncCompleted, onCompleted)
    return () => {
      window.removeEventListener(OFFLINE_EVENTS.queueChanged, onChange)
      window.removeEventListener(OFFLINE_EVENTS.conflict, onConflict as EventListener)
      window.removeEventListener(OFFLINE_EVENTS.syncCompleted, onCompleted)
    }
  }, [refresh])

  // Initial load + when user changes
  useEffect(() => { refresh() }, [refresh])

  // Auto-flush on reconnect
  const retryNow = useCallback(async () => {
    if (!user?.id || flushingRef.current) return
    if (!net.isOnline) {
      const ok = await net.checkNow()
      if (!ok) return
    }
    flushingRef.current = true
    try {
      await flushQueue(user.id)
    } finally {
      flushingRef.current = false
    }
  }, [user?.id, net])

  useEffect(() => {
    if (net.isOnline && pendingCount > 0 && !flushingRef.current) {
      retryNow()
    }
  }, [net.isOnline, pendingCount, retryNow])

  const resolveKeepMine = useCallback(async () => {
    if (!currentConflict) return
    try { trackConversion('offline_conflict_resolved_keep_mine', { feature: currentConflict.mutation.type }) } catch {}
    await resolveConflictKeepMine(currentConflict.mutation.id)
    setCurrentConflict(null)
    retryNow()
  }, [currentConflict, retryNow])

  const resolveUseTheirs = useCallback(async () => {
    if (!currentConflict) return
    try { trackConversion('offline_conflict_resolved_use_theirs', { feature: currentConflict.mutation.type }) } catch {}
    await resolveConflictUseTheirs(currentConflict.mutation.id)
    setCurrentConflict(null)
    retryNow()
  }, [currentConflict, retryNow])

  const dismissConflict = useCallback(() => {
    setCurrentConflict(null)
  }, [])

  return {
    ...net,
    pendingCount,
    deadCount,
    currentConflict,
    lastSyncAt,
    retryNow,
    resolveKeepMine,
    resolveUseTheirs,
    dismissConflict,
  }
}
